import React, { useEffect, useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { toast } from "react-toastify";
import { useAuth } from "../../../../hooks";
import { EnergyPerformanceInstallationform } from "../../../../api";
import "./InstallationPicker.scss";

const energyPerformanceCtrl = new EnergyPerformanceInstallationform();

// Qué instalación se ve en el módulo (hoja 23 p. 27): la planta del sitio, uno de los 22 ejemplos (globales, de
// solo lectura) o una copia para practicar de un ejemplo. Las copias nunca se mezclan con la planta real.
// sel: "real" | "ej:<id del ejemplo>" | "pr:<_id de la copia>"
export function useInstalaciones(site) {
  const { accessToken } = useAuth();
  const [real, setReal] = useState(undefined); // undefined = cargando, null = sin instalación
  const [sinAcceso, setSinAcceso] = useState(false); // el servidor rechazó el sitio: no es lo mismo que «sin instalación»
  const [copias, setCopias] = useState([]);
  const [ejemplos, setEjemplos] = useState([]);
  const [ejemplo, setEjemplo] = useState(null); // documento del ejemplo elegido
  const [sel, setSel] = useState("real");
  const [ocupado, setOcupado] = useState(false);

  // Al cambiar de sitio se vacía todo ya: nunca se muestra lo del sitio anterior, y una respuesta que llega tarde se descarta.
  useEffect(() => {
    let vigente = true;
    setReal(undefined);
    setCopias([]);
    setEjemplo(null);
    setSel("real");
    setSinAcceso(false);
    if (!site) return undefined;
    (async () => {
      try {
        const [res, practica] = await Promise.all([
          energyPerformanceCtrl.getEnergyPerformanceInstallationsBySite(accessToken, site),
          energyPerformanceCtrl.getPracticeBySite(accessToken, site),
        ]);
        if (!vigente) return;
        setReal((res.installations || [])[0] || null);
        setCopias(practica.installations || []);
      } catch (error) {
        if (!vigente) return;
        console.error(error);
        setReal(null);
        setSinAcceso(true);
      }
    })();
    return () => { vigente = false; };
  }, [site, accessToken]);

  useEffect(() => {
    energyPerformanceCtrl.getExamples(accessToken).then((r) => setEjemplos(r.examples || [])).catch(() => setEjemplos([]));
  }, [accessToken]);

  const elegir = async (valor) => {
    if (valor.startsWith("ej:")) {
      setOcupado(true);
      try {
        const res = await energyPerformanceCtrl.getExample(accessToken, valor.slice(3));
        setEjemplo(res.installation);
        setSel(valor);
      } catch (error) {
        toast.error(error?.message || "Error");
      } finally {
        setOcupado(false);
      }
    } else setSel(valor);
  };

  const installation = sel === "real" ? real
    : sel.startsWith("ej:") ? ejemplo
      : copias.find((c) => `pr:${c._id}` === sel) || null;

  // lo que guarda una pantalla vuelve a su lugar (planta real o copia)
  const onSaved = (doc) => {
    if (!doc || (doc.site && String(doc.site) !== String(site))) return; // respuesta de un guardado del sitio anterior
    if (doc.practiceOf) setCopias((l) => l.map((c) => (c._id === doc._id ? doc : c)));
    else setReal(doc);
  };

  const duplicar = async () => {
    if (!ejemplo) return;
    setOcupado(true);
    try {
      const res = await energyPerformanceCtrl.createPractice(accessToken, ejemplo.code, site);
      setCopias((l) => [res.installation, ...l]);
      setSel(`pr:${res.installation._id}`);
      return res.installation;
    } catch (error) {
      toast.error(error?.message || "Error");
      return null;
    } finally {
      setOcupado(false);
    }
  };

  const restablecer = async () => {
    if (!installation?._id) return;
    setOcupado(true);
    try {
      const res = await energyPerformanceCtrl.resetInstallation(accessToken, installation._id);
      onSaved(res.installation);
      return true;
    } catch (error) {
      toast.error(error?.message || "Error");
      return false;
    } finally {
      setOcupado(false);
    }
  };

  const borrarCopia = async () => {
    if (!installation?.practiceOf) return;
    setOcupado(true);
    try {
      await energyPerformanceCtrl.deleteEnergyPerformanceInstallation(accessToken, installation._id);
      setCopias((l) => l.filter((c) => c._id !== installation._id));
      setSel("real");
    } catch (error) {
      toast.error(error?.message || "Error");
    } finally {
      setOcupado(false);
    }
  };

  return { real, sinAcceso, copias, ejemplos, sel, elegir, installation, onSaved, duplicar, restablecer, borrarCopia, ocupado };
}

// puedeEditar: sin permiso, «Duplicar», «Restablecer» y «Borrar» se ven deshabilitados con el motivo (hoja 23 p. 66).
export function InstallationPicker({ estado, t, puedeEditar = true }) {
  const { real, copias, ejemplos, sel, elegir, installation, duplicar, restablecer, borrarCopia, ocupado } = estado;
  const [confirmar, setConfirmar] = useState(null); // "restablecer" | "borrar"
  const esEjemplo = sel.startsWith("ej:");
  const esCopia = sel.startsWith("pr:");
  const sinPermiso = puedeEditar ? undefined : t("energy_performance_no_permission");

  const aceptar = async () => {
    const accion = confirmar;
    setConfirmar(null);
    if (accion === "restablecer" && await restablecer()) toast.success(t("energy_performance_reset_done"));
    if (accion === "borrar") await borrarCopia();
  };

  return (
    <div className="ep-picker">
      <label className="ep-picker__label">
        {t("energy_performance_picker_label")}
        <select value={sel} disabled={ocupado} onChange={(e) => elegir(e.target.value)} aria-label={t("energy_performance_picker_label")}>
          <option value="real">{t("energy_performance_picker_site")}: {real ? real.name || real.code : t("energy_performance_picker_site_empty")}</option>
          {copias.length > 0 && (
            <optgroup label={t("energy_performance_picker_practice")}>
              {copias.map((c) => <option key={c._id} value={`pr:${c._id}`}>{c.name} · {c.code}</option>)}
            </optgroup>
          )}
          {ejemplos.length > 0 && (
            <optgroup label={t("energy_performance_picker_examples").replace("{n}", ejemplos.length)}>
              {ejemplos.map((e) => <option key={e.id} value={`ej:${e.id}`}>{e.name}</option>)}
            </optgroup>
          )}
        </select>
      </label>

      <div className="ep-picker__acciones">
        {esEjemplo && (
          <Button type="button" primary size="small" loading={ocupado} disabled={ocupado || !installation || !puedeEditar} title={sinPermiso} onClick={duplicar}>
            <Icon name="copy outline" /> {t("energy_performance_practice_duplicate")}
          </Button>
        )}
        {installation && installation._id && (
          <Button type="button" basic size="small" disabled={ocupado || !puedeEditar} title={sinPermiso} onClick={() => setConfirmar("restablecer")}>
            <Icon name="undo" /> {t("energy_performance_reset")}
          </Button>
        )}
        {esCopia && (
          <Button type="button" basic size="small" disabled={ocupado || !puedeEditar} title={sinPermiso} onClick={() => setConfirmar("borrar")}>
            <Icon name="trash alternate outline" /> {t("energy_performance_practice_delete")}
          </Button>
        )}
      </div>

      {!puedeEditar && <p className="ep-picker__nota" role="note">{sinPermiso}</p>}
      {esEjemplo && <p className="ep-picker__nota" role="note">{t("energy_performance_example_readonly")}</p>}
      {esCopia && <p className="ep-picker__nota" role="note">{t("energy_performance_practice_note")}</p>}

      <Confirm
        open={!!confirmar}
        content={confirmar === "borrar" ? t("energy_performance_practice_delete_confirm") : t("energy_performance_reset_confirm")}
        cancelButton={t("cancel")}
        confirmButton={confirmar === "borrar" ? t("energy_performance_practice_delete") : t("energy_performance_reset")}
        onCancel={() => setConfirmar(null)}
        onConfirm={aceptar}
      />
    </div>
  );
}
